import type { Theme } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { Schemas } from "../../schemas";
import { useUpdateAlertSchema } from "../../hooks/mutation/update/useUpdateAlertSchema";
import { useAlertProviders } from "../../hooks/query/useAlertProviders";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = Schemas.alertSchema.omit({ id: true, system: true });

const EditAlertSchemaDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  alertSchemaId: string;
}> = ({ onDialogClosed, alertSchemaId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const { data: alertProviders = [] } = useAlertProviders();
  const existing = React.useMemo(
    () => alertProviders.find((p) => p.id === alertSchemaId),
    [alertProviders, alertSchemaId],
  );

  const { mutateAsync: updateAlertSchema, isPending: isUpdating } =
    useUpdateAlertSchema();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [foreignKeyInputValue, setForeignKeyInputValue] =
    React.useState<string>("");

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      foreignKeyProps: [],
      statusProp: "",
      statusMappingCritical: "critical",
      statusMappingWarning: "warning",
      statusMappingResolved: "resolved",
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    if (!existing) {return;}
    reset({
      name: existing.name,
      description: existing.description ?? "",
      foreignKeyProps: existing.foreignKeyProps,
      statusProp: existing.statusProp,
      statusMappingCritical: existing.statusMappingCritical,
      statusMappingWarning: existing.statusMappingWarning,
      statusMappingResolved: existing.statusMappingResolved,
    });
  }, [existing, reset]);

  const onSubmit = async (data: z.infer<typeof schema>) => {
    try {
      setSubmitError(null);
      await updateAlertSchema({ alertSchemaId, alertSchema: data });
      onDialogClosed({ message: "Alert schema updated!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onInvalid = (errs: Record<string, unknown>) => {
    console.error(errs);
    setSubmitError("Error during submit");
  };

  const onClose = () => {
    if (isUpdating) {return;}
    setSubmitError(null);
    reset({});
    onDialogClosed();
  };

  const editDisabled = !existing || !!existing.system;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg },
      }}
    >
      <StandardDialogTitle
        title={"Edit Alert Schema"}
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box
        component="form"
        id="edit-alert-schema-form"
        onSubmit={handleSubmit(onSubmit, onInvalid)}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create a alert schema and optionally declare its dependencies.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              {editDisabled && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {existing?.system
                    ? "System alert schemas cannot be edited."
                    : "Alert schema not found."}
                </FormHelperText>
              )}

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}

              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Coralogix"
                  error={!!errors.name}
                  disabled={editDisabled}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl error={!!errors.foreignKeyProps}>
                <FormLabel>Foreign keys</FormLabel>

                <Controller
                  name="foreignKeyProps"
                  control={control}
                  render={({ field }) => {
                    const confirmAdd = () => {
                      const v = foreignKeyInputValue.trim();
                      if (!v) {return;}

                      const current = ((field.value ?? []) as string[])
                        .map((x) => x.trim())
                        .filter(Boolean);

                      const next = Array.from(new Set([...current, v]));
                      field.onChange(next);

                      setForeignKeyInputValue("");
                      if (submitError) {setSubmitError(null);}
                    };

                    return (
                      <Autocomplete
                        multiple
                        freeSolo
                        disableClearable
                        options={[] as string[]}
                        value={(field.value ?? []) as string[]}
                        disabled={editDisabled}
                        inputValue={foreignKeyInputValue}
                        onInputChange={(_, newInput, reason) => {
                          if (reason === "reset") {return;}
                          setForeignKeyInputValue(newInput ?? "");
                        }}
                        onChange={(_, newValue) => {
                          const unique = Array.from(
                            new Set(
                              newValue.map((v) => v.trim()).filter(Boolean),
                            ),
                          );
                          field.onChange(unique);
                          if (submitError) {setSubmitError(null);}
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option}
                              size="small"
                              {...getTagProps({ index })}
                              key={`${option}-${index}`}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Type a key and press Enter or Add"
                            error={!!errors.foreignKeyProps}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                confirmAdd();
                              }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <Button
                                      color="primary"
                                      variant="contained"
                                      size="small"
                                      disabled={
                                        editDisabled ||
                                        foreignKeyInputValue.trim().length === 0
                                      }
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={confirmAdd}
                                      startIcon={<AddRoundedIcon fontSize="small" />}
                                    >
                                      Add
                                    </Button>
                                  </InputAdornment>
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    );
                  }}
                />

                <FormHelperText>
                  {errors.foreignKeyProps?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Status property</FormLabel>
                <TextField
                  size="small"
                  placeholder="The property which identify the status"
                  error={!!errors.statusProp}
                  disabled={editDisabled}
                  {...register("statusProp", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.statusProp}>
                  {errors.statusProp?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Critical status mapping</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. critical"
                  error={!!errors.statusMappingCritical}
                  disabled={editDisabled}
                  {...register("statusMappingCritical", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.statusMappingCritical}>
                  {errors.statusMappingCritical?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Warning status mapping</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. warning"
                  error={!!errors.statusMappingWarning}
                  disabled={editDisabled}
                  {...register("statusMappingWarning", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.statusMappingWarning}>
                  {errors.statusMappingWarning?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Resolved status mapping</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. resolved"
                  error={!!errors.statusMappingResolved}
                  disabled={editDisabled}
                  {...register("statusMappingResolved", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.statusMappingResolved}>
                  {errors.statusMappingResolved?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the system"
                  error={!!errors.description}
                  disabled={editDisabled}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Save"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isUpdating}
          disabled={editDisabled || isUpdating}
        />
      </Box>
    </Dialog>
  );
};

export default EditAlertSchemaDialog;
